import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {CustomTranslateService} from './custom-translate.service';
import {LANGUAGE_OPTIONS} from '../constants/language-options';
import {LanguageOption} from '../model/language-option.model';
import {InitResponse} from '../model/init-response.model';
import {environment} from '../../../environments/environment';

const DEFAULT_LANGUAGE = 'de';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private readonly bootstrapped$ = new BehaviorSubject<boolean>(false);
  private readonly currentLangSubject = new BehaviorSubject<string>(DEFAULT_LANGUAGE);

  readonly isBootstrapped$ = this.bootstrapped$.asObservable();
  readonly currentLang$ = this.currentLangSubject.asObservable();

  languageOptions: LanguageOption[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private customTranslate: CustomTranslateService,
    private translate: TranslateService
  ) {}

  async initialize(): Promise<void> {
    try {
      const response = await firstValueFrom(this.initWithTokenFromFragment());
      const availableLanguages = this.buildAvailableLanguages(response);
      const defaultLanguage = response.preferredLang
        ? response.preferredLang.toLowerCase()
        : DEFAULT_LANGUAGE;
      this.languageOptions = this.buildLanguageOptions(availableLanguages);

      this.customTranslate.addLangs(availableLanguages);
      this.customTranslate.setDefaultLang(defaultLanguage);

      const storedLang = this.customTranslate.getLangFromLocalStorage();
      const selected = this.isLanguageAvailable(storedLang, availableLanguages)
        ? storedLang!.toLowerCase()
        : availableLanguages[0] ?? defaultLanguage;

      this.applyLanguage(selected);
    } catch {
      await this.router.navigateByUrl('/page-not-found', {replaceUrl: true});
    } finally {
      this.cleanupFragment();
      this.bootstrapped$.next(true);
    }
  }

  setLanguage(lang: string): void {
    if (!this.isLanguageAvailable(lang, this.languageOptions.map(option => option.value))) {
      return;
    }
    this.applyLanguage(lang);
    this.customTranslate.setLangToLocalStorage(lang);
  }

  get currentLang(): string {
    return this.currentLangSubject.value;
  }

  private initWithTokenFromFragment() {
    const token = this.extractTokenFromFragment();
    const headers = token ? new HttpHeaders({Authorization: `Bearer ${token}`}) : undefined;
    return this.http.post<InitResponse>(
      `${environment.appointmentsApiURL}/api/v1/init`,
      null,
      {
        headers,
        withCredentials: true
      }
    );
  }

  private extractTokenFromFragment(): string | null {
    const fragment = this.router.parseUrl(this.router.url).fragment ?? window.location.hash.slice(1);
    if (!fragment) {
      return null;
    }
    const match = fragment.match(/(?:^|&)t=([A-Za-z0-9._~\-$]+)/);
    return match?.[1] ?? null;
  }

  private cleanupFragment(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }

  private buildAvailableLanguages(response: InitResponse): string[] {
    const languages = [
      response.preferredLang,
      ...(Array.isArray(response.additionalLangs) ? response.additionalLangs : [])
    ]
      .filter(Boolean)
      .map(lang => String(lang).toLowerCase());

    const unique = Array.from(new Set(languages));
    return unique.length ? unique : [DEFAULT_LANGUAGE];
  }

  private buildLanguageOptions(codes: string[]): LanguageOption[] {
    return codes.map(code => {
      const match = LANGUAGE_OPTIONS.find(option => option.value === code);
      if (match) {
        return match;
      }
      return {
        value: code,
        viewValue: code.toUpperCase(),
        flagIconClass: ''
      };
    });
  }

  private applyLanguage(lang: string) {
    this.customTranslate.use(lang);
    this.currentLangSubject.next(lang);
  }

  private isLanguageAvailable(lang: string | null | undefined, availableLanguages: string[]): boolean {
    if (!lang) return false;
    return availableLanguages.includes(lang.toLowerCase());
  }
}
